/**
 * The damage-chart model of a `DAMAGE` work order (SWOOP-335, WOW 3 Part B). A damage is a numbered mark
 * placed exactly where the fleet manager clicked the generic vehicle drawing, recording WHERE the damage
 * is (an {@link DamageArea area} and a finer {@link DamageElement element}), WHAT it is ({@link DamageType}),
 * an optional {@link DamageAction} and note, and at least one photo (stored as `work_order` documents, see
 * `DOCUMENT_TYPE.WORK_ORDER_DAMAGE_PHOTO`). Damages are embedded on the work order (`WorkOrder.damages`),
 * never a collection of their own, and are read with it.
 *
 * The element codes are promoted from the front-office generic drawing
 * (`features/work-orders/components/damage-map/vehicle-diagram-parts.ts`): the two front seats were named
 * `driver_seat` / `passenger_seat` there and are renamed `front_seat_left` / `front_seat_right` here
 * (Zada: no "driver" / "passenger"). Product will send the final list (doc Q29); adding a code later is
 * additive. Each element belongs to exactly one area, and each area to exactly one drawing view.
 */

import { WORK_ORDER_STATUS } from './work-order.interface';
import type { WorkOrderStatus } from './work-order.interface';

/** The coarse region of the vehicle a damage sits in (SWOOP-335). Prefilled from the clicked part (doc Q26). */
export const DAMAGE_AREA = {
  FRONT: 'FRONT',
  INTERIOR: 'INTERIOR',
  LEFT: 'LEFT',
  REAR: 'REAR',
  RIGHT: 'RIGHT',
  ROOF: 'ROOF',
} as const;

export type DamageArea = (typeof DAMAGE_AREA)[keyof typeof DAMAGE_AREA];

export const DAMAGE_AREAS = Object.values(DAMAGE_AREA);

/** Which drawing a damage was marked on (SWOOP-335): the exterior body or the interior cabin. */
export const DAMAGE_VIEW = {
  EXTERIOR: 'exterior',
  INTERIOR: 'interior',
} as const;

export type DamageView = (typeof DAMAGE_VIEW)[keyof typeof DAMAGE_VIEW];

export const DAMAGE_VIEWS = Object.values(DAMAGE_VIEW);

/**
 * Every named part of the generic drawing mapped to the {@link DamageArea} it belongs to (SWOOP-335). The
 * key is the stable element code (also the front-office `data-part` attribute and the i18n leaf); the value
 * is its area. Interior parts map to `INTERIOR`; every other part to its side of the body. Keep in sync with
 * the front-office drawing and the i18n `damageMap` block.
 */
export const DAMAGE_ELEMENT = {
  boot: DAMAGE_AREA.REAR,
  boot_floor: DAMAGE_AREA.INTERIOR,
  centre_console: DAMAGE_AREA.INTERIOR,
  dashboard: DAMAGE_AREA.INTERIOR,
  door_front_left: DAMAGE_AREA.LEFT,
  door_front_right: DAMAGE_AREA.RIGHT,
  door_rear_left: DAMAGE_AREA.LEFT,
  door_rear_right: DAMAGE_AREA.RIGHT,
  front_bumper: DAMAGE_AREA.FRONT,
  front_seat_left: DAMAGE_AREA.INTERIOR,
  front_seat_right: DAMAGE_AREA.INTERIOR,
  headlight_left: DAMAGE_AREA.FRONT,
  headlight_right: DAMAGE_AREA.FRONT,
  hood: DAMAGE_AREA.FRONT,
  mirror_left: DAMAGE_AREA.LEFT,
  mirror_right: DAMAGE_AREA.RIGHT,
  rear_bumper: DAMAGE_AREA.REAR,
  rear_seat_centre: DAMAGE_AREA.INTERIOR,
  rear_seat_left: DAMAGE_AREA.INTERIOR,
  rear_seat_right: DAMAGE_AREA.INTERIOR,
  rear_window: DAMAGE_AREA.REAR,
  roof: DAMAGE_AREA.ROOF,
  steering_wheel: DAMAGE_AREA.INTERIOR,
  taillight_left: DAMAGE_AREA.REAR,
  taillight_right: DAMAGE_AREA.REAR,
  wheel_front_left: DAMAGE_AREA.LEFT,
  wheel_front_right: DAMAGE_AREA.RIGHT,
  wheel_rear_left: DAMAGE_AREA.LEFT,
  wheel_rear_right: DAMAGE_AREA.RIGHT,
  windscreen: DAMAGE_AREA.FRONT,
} as const;

export type DamageElement = keyof typeof DAMAGE_ELEMENT;

export const DAMAGE_ELEMENTS = Object.keys(DAMAGE_ELEMENT) as DamageElement[];

/** Type guard: is `value` a known damage element code? */
export function isDamageElement(value: string | null | undefined): value is DamageElement {
  return value != null && Object.prototype.hasOwnProperty.call(DAMAGE_ELEMENT, value);
}

/** The elements that belong to a given area, in declaration order — the UI's option list for that area. */
export function damageElementsForArea(area: DamageArea): DamageElement[] {
  return DAMAGE_ELEMENTS.filter((element) => DAMAGE_ELEMENT[element] === area);
}

/** Whether an element belongs to a given area (`element` must be in `area`, per the drawing). */
export function isDamageElementInArea(element: DamageElement, area: DamageArea): boolean {
  return DAMAGE_ELEMENT[element] === area;
}

/**
 * The elements of each area, keyed by area (SWOOP-335): the source of truth the UI filters its element
 * picker by once an area is chosen, and the backend validates `element` belongs to `area` against. Derived
 * from {@link DAMAGE_ELEMENT}, so the two cannot drift.
 */
export const DAMAGE_ELEMENTS_BY_AREA: Record<DamageArea, DamageElement[]> = {
  [DAMAGE_AREA.FRONT]: damageElementsForArea(DAMAGE_AREA.FRONT),
  [DAMAGE_AREA.INTERIOR]: damageElementsForArea(DAMAGE_AREA.INTERIOR),
  [DAMAGE_AREA.LEFT]: damageElementsForArea(DAMAGE_AREA.LEFT),
  [DAMAGE_AREA.REAR]: damageElementsForArea(DAMAGE_AREA.REAR),
  [DAMAGE_AREA.RIGHT]: damageElementsForArea(DAMAGE_AREA.RIGHT),
  [DAMAGE_AREA.ROOF]: damageElementsForArea(DAMAGE_AREA.ROOF),
};

/** What the damage is (SWOOP-335, doc Q19). Drafted from the design; product will confirm the final list (Q29). */
export const DAMAGE_TYPE = {
  BROKEN: 'BROKEN',
  CRACK: 'CRACK',
  DENT: 'DENT',
  OTHER: 'OTHER',
  SCRATCH: 'SCRATCH',
  STAIN: 'STAIN',
  TEAR: 'TEAR',
} as const;

export type DamageType = (typeof DAMAGE_TYPE)[keyof typeof DAMAGE_TYPE];

export const DAMAGE_TYPES = Object.values(DAMAGE_TYPE);

/**
 * The optional treatment a damage calls for (SWOOP-335, doc Q33). `MONITOR` means "noticed, look at it at a
 * later checkup" — a monitored damage can still be ticked Fixed (= checked, no treatment needed). Optional:
 * a damage may carry no action.
 */
export const DAMAGE_ACTION = {
  MONITOR: 'MONITOR',
  REPAIR: 'REPAIR',
  REPLACE: 'REPLACE',
} as const;

export type DamageAction = (typeof DAMAGE_ACTION)[keyof typeof DAMAGE_ACTION];

/** In display order (the design's Repair / Replace / Monitor toggle), not alphabetical. */
export const DAMAGE_ACTIONS = [DAMAGE_ACTION.REPAIR, DAMAGE_ACTION.REPLACE, DAMAGE_ACTION.MONITOR];

/** At most 30 damages per work order (SWOOP-335): a generous ceiling on one accident's marks. */
export const WORK_ORDER_DAMAGE_MAX = 30;

/**
 * The work-order statuses in which a damage may be added, edited or deleted (SWOOP-335): OPEN or RECEIVED.
 * The single source for the backend's guarded damage writes, the damage service's pre-checks and the front
 * office's chart (which hides the edit affordances outside them).
 */
export const WORK_ORDER_DAMAGE_EDITABLE_STATUSES: readonly WorkOrderStatus[] = [
  WORK_ORDER_STATUS.OPEN,
  WORK_ORDER_STATUS.RECEIVED,
];

/**
 * The work-order statuses in which a damage's Fixed tick may be set or cleared (SWOOP-335, FR-015c): OPEN,
 * RECEIVED or AT_GARAGE — locked from DONE. Wider than {@link WORK_ORDER_DAMAGE_EDITABLE_STATUSES}: a garage
 * fixes a damage while the vehicle is with it.
 */
export const WORK_ORDER_DAMAGE_FIXED_STATUSES: readonly WorkOrderStatus[] = [
  WORK_ORDER_STATUS.OPEN,
  WORK_ORDER_STATUS.RECEIVED,
  WORK_ORDER_STATUS.AT_GARAGE,
];

/** Max length of a damage note (SWOOP-335, doc Q19). */
export const WORK_ORDER_DAMAGE_NOTE_MAX = 1000;

/**
 * One damage mark on a `DAMAGE` work order's chart (SWOOP-335), embedded in {@link WorkOrder.damages} with
 * its own `_id`. `number` is handed out from the work order's `damageSequence` counter, is immutable and is
 * never reused after a delete (doc Q32). `x` / `y` are the click position as a fraction (0–1) of the
 * drawing's width / height, so the mark sits exactly where it was clicked (doc Q25). `fixedAt` / `fixedBy`
 * are set and cleared by the Fixed tick, independently of the items' verification and whatever the action.
 */
export interface WorkOrderDamageMark {
  /** The optional treatment (Repair / Replace / Monitor), or null (doc Q33). */
  action?: DamageAction | null;
  /** The coarse region; prefilled from the clicked part, changeable (doc Q26). */
  area: DamageArea;
  /** When the damage was recorded (server-stamped). */
  createdAt: Date;
  /** The user who recorded the damage (`users` row id, ObjectId hex). */
  createdBy: string;
  /** What the damage is. */
  damageType: DamageType;
  /** The finer part; must belong to {@link area} (per {@link DAMAGE_ELEMENTS_BY_AREA}). */
  element: DamageElement;
  /** When the damage was ticked Fixed, or null while unfixed (doc Q21/Q34). */
  fixedAt?: Date | null;
  /** The user who ticked it Fixed (`users` row id, ObjectId hex), or null while unfixed. */
  fixedBy?: string | null;
  /** The embedded row's id (ObjectId hex), addressed by `/work-orders/:id/damages/:damageId`. */
  id: string;
  /** An optional free-text note (≤ {@link WORK_ORDER_DAMAGE_NOTE_MAX}), or null. */
  note?: string | null;
  /** The display number, ≥ 1, from `damageSequence`; immutable and never reused (doc Q31/Q32). */
  number: number;
  /** When the damage was last edited (server-stamped). */
  updatedAt: Date;
  /** Which drawing the mark was placed on. */
  view: DamageView;
  /** The click position's x fraction of the drawing width (0–1), exactly where clicked (doc Q25). */
  x: number;
  /** The click position's y fraction of the drawing height (0–1), exactly where clicked (doc Q25). */
  y: number;
}

/** A blob staged under a `work-orders` SAS, sent with a damage write (SWOOP-335). Size / MIME read from the stored blob. */
export interface DamageStagedBlob {
  blobPath: string;
  fileName: string;
}

/** The body of `POST /work-orders/:id/damages` (SWOOP-335): create a damage with 1–10 staged photos. */
export interface CreateWorkOrderDamage {
  action?: DamageAction | null;
  area: DamageArea;
  damageType: DamageType;
  element: DamageElement;
  note?: string | null;
  photos: DamageStagedBlob[];
  view: DamageView;
  x: number;
  y: number;
}

/**
 * The body of `PATCH /work-orders/:id/damages/:damageId` (SWOOP-335): edit a damage's fields, add photos and
 * remove some, keeping at least one. An omitted field is left unchanged; an explicit `null` on `action` /
 * `note` clears it. `view` / `x` / `y` are immutable (the mark does not move), so they are not editable.
 */
export interface UpdateWorkOrderDamage {
  action?: DamageAction | null;
  addPhotos?: DamageStagedBlob[];
  area?: DamageArea;
  damageType?: DamageType;
  element?: DamageElement;
  note?: string | null;
  removePhotoIds?: string[];
}
